import { useSnackbar } from "notistack";
import React from "react";
import { ApiError } from "../../Api/ApiError";
import { getSystemValue, setSystemValue, SystemValueInterface } from "../../Api/System";
import { SystemObjectStore } from "../../ObjectStore/SystemObjectStore";
import PageTitle from "../atoms/PageTitle";
import CouponSystemSettings from "../organisms/CouponSystemSettings";
import MembershipSettings from "../organisms/MembershipSettings";
import OnlineLessonSettings from "../organisms/OnlineLessonSettings";
import ReserveSystemSettings from "../organisms/ReserveSystemSettings";
import ChargeCategorySettings from "../organisms/ChargeCategorySettings";
import CancelSystemSettings from "../organisms/CancelSystemSettings";
import LogoImageSettings from "../organisms/LogoImageSettings";
import CashRegisterSettings from "../organisms/CashRegisterSettings";
import MailSystemSettings from "../organisms/MailSystemSettings";

const SettingPage: React.FC = () => {
    const { systemObject } = React.useContext(SystemObjectStore)
    const [sysValue, setSysValue] = React.useState<SystemValueInterface>()
    const { enqueueSnackbar } = useSnackbar()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const loadSystemValue = React.useCallback(() => {
        getSystemValue({site: "system_manager"}, {signal: abortController.signal}).then(datas=>{
            setSysValue(datas)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    },[abortController, enqueueSnackbar])

    const doSave = React.useCallback((key: string, value: string)=>{
        const controller = new AbortController()
        setSystemValue({
            key: key,
            value: value
        }, {signal: controller.signal}).then(()=>{
            enqueueSnackbar("保存しました", {variant: 'success'})
            loadSystemValue()
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    },[enqueueSnackbar, loadSystemValue])

    React.useEffect(() => {
        loadSystemValue()
    }, [loadSystemValue])

    return (
        <>
            <PageTitle text="システム設定" />
            <ReserveSystemSettings
                systemValue={sysValue}
                onSave={doSave}
            />
            <MembershipSettings
                systemValue={sysValue}
                onSave={doSave}
            />
            <ChargeCategorySettings
                systemValue={sysValue}
                onSave={doSave}
            />
            <CancelSystemSettings
                systemValue={sysValue}
                onSave={doSave}
            />
            <CouponSystemSettings
                systemValue={sysValue}
                onSave={doSave}
            />
            <LogoImageSettings
                systemValue={sysValue}
                onSave={doSave}
            />
            <CashRegisterSettings
                systemValue={sysValue}
                onSave={doSave}
            />
            <MailSystemSettings
                systemValue={sysValue}
                onSave={doSave}
            />
            {(systemObject.databaseVersion && systemObject.databaseVersion >= 11) &&
                <OnlineLessonSettings
                    systemValue={sysValue}
                    onSave={doSave}
                />
            }
        </>
    )
}

export default SettingPage